import {
  Injectable,
  PLATFORM_ID,
  inject
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

declare const L: any;

@Injectable({
  providedIn: 'root'
})
export class MapLoaderService {

  private readonly platformId =
    inject(PLATFORM_ID);

  private loadPromise: Promise<any> | null = null;


  /* =========================
     LOAD LEAFLET (ONCE)
  ========================= */

  load(): Promise<any> {

    if (!isPlatformBrowser(this.platformId)) {
      return Promise.reject(
        'Map is only available in the browser'
      );
    }

    if (typeof L !== 'undefined') {
      return Promise.resolve(L);
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {

      const script =
        document.createElement('script');

      script.src =
        'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

      script.async = true;

      script.onload = () => resolve(L);

      script.onerror = () =>
        reject('Failed to load map library');

      document.body.appendChild(script);
    });

    return this.loadPromise;
  }


  /* =========================
     REVERSE GEOCODE
     (lat/lng -> address text)
  ========================= */

  async reverseGeocode(
    lat: number,
    lng: number
  ): Promise<string> {

    try {

      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=14`
      );

      const data = await res.json();

      return data?.display_name || '';

    } catch {

      return '';
    }
  }


  /* =========================
     REVERSE GEOCODE — SHORT AREA LABEL
     (navbar-এর "current location" এর জন্য —
     শুধু "Dhaka" না দেখিয়ে "Mirpur, Dhaka"-র
     মতো area-level নাম বের করে)
  ========================= */

  async reverseGeocodeArea(
    lat: number,
    lng: number
  ): Promise<{ area: string; city: string; road: string }> {

    try {

      // ⚠️ FIX: আগে zoom=18 (building-level) ব্যবহার হতো — Nominatim
      // তখন coordinate-এর সবচেয়ে কাছের building/POI (কোনো ছোট
      // দোকান/কমপ্লেক্সের নাম, যেমন "Paradogar") খুঁজে সেটার address
      // ফেরত দিত, যেটা মানুষ সাধারণত এলাকা হিসেবে চেনে না। zoom=14
      // মানে Nominatim সরাসরি neighbourhood/এলাকা-লেভেলের OSM
      // polygon-টা ম্যাচ করে, তাই সাধারণভাবে পরিচিত এলাকার নাম
      // (যেমন "Matuail") আসার সম্ভাবনা অনেক বেশি — ঠিক Chaldal-এর
      // নিজস্ব area-ডেটাবেজ যেভাবে কাজ করে তার কাছাকাছি ফলাফল দেয়
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1&zoom=14`
      );

      const data = await res.json();

      const addr = data?.address || {};

      // Nominatim বিভিন্ন জায়গার জন্য বিভিন্ন field ব্যবহার করে —
      // এলাকার নাম (Mirpur, Konapara ইত্যাদি) কখনো suburb/
      // neighbourhood-এ থাকে, কখনো quarter/hamlet/residential-এ
      // (এলাকাটা OpenStreetMap-এ কতটা বিস্তারিতভাবে ম্যাপ করা
      // আছে তার উপর নির্ভর করে — ঢাকার বাইরের/কম-ম্যাপ করা এলাকায়
      // মাঝেমধ্যে কাছাকাছি বড় এলাকার নাম আসতে পারে, এটা GPS-এর
      // সমস্যা না, OpenStreetMap-এর data coverage-এর সীমাবদ্ধতা)
      const area =
        addr.neighbourhood ||
        addr.suburb ||
        addr.quarter ||
        addr.hamlet ||
        addr.residential ||
        addr.city_district ||
        addr.borough ||
        addr.town ||
        addr.village ||
        '';

      const city =
        addr.city ||
        addr.state_district ||
        addr.state ||
        '';

      // 🆕 ROAD/SECTOR — শুধু precise GPS ("Use My Location") এর
      // জন্য ব্যবহার হয়, IP-based auto-detect-এ না (IP location এমনিতেই
      // এত নির্ভুল না যে road-level দেখানো অর্থবহ হবে)। Nominatim-এ
      // road আলাদা field, আর "Sector" ঢাকার Uttara-এর মতো এলাকায়
      // road field-এর ভেতরেই আসে (যেমন "Sector 10 Road 5")।
      const road =
        addr.road ||
        addr.pedestrian ||
        '';

      return { area, city, road };

    } catch {

      return { area: '', city: '', road: '' };
    }
  }


  /* =========================
     KNOWN LOCAL AREAS (OVERRIDE)
     ফ্রি Nominatim/OpenStreetMap সার্চ বাংলাদেশের অনেক
     পরিচিত এলাকার নাম ঠিকভাবে খুঁজে পায় না (data coverage
     কম) — যেমন "Matuail" লিখলে ভুল করে পাশের "Rayer Bag"
     দেখিয়ে দেয়। Chaldal-এর মতো সাইট তাই নিজস্ব এলাকার
     তালিকা রাখে। এখানে সেরকম একটা ছোট override তালিকা —
     ভবিষ্যতে একই সমস্যা হওয়া কোনো এলাকা পেলে এখানে যোগ
     করে দিলেই হবে।
  ========================= */

  private readonly knownAreas: Array<{
    name: string;
    lat: number;
    lng: number;
  }> = [
    { name: 'Matuail', lat: 23.7121, lng: 90.4598 }
  ];

  private matchKnownArea(
    query: string
  ): { label: string; lat: number; lng: number } | null {

    const q = query.trim().toLowerCase();

    if (!q) {
      return null;
    }

    const found = this.knownAreas.find(
      a =>
        a.name.toLowerCase() === q ||
        a.name.toLowerCase().startsWith(q)
    );

    if (!found) {
      return null;
    }

    return {
      label: `${found.name}, Dhaka`,
      lat: found.lat,
      lng: found.lng
    };
  }


  /* =========================
     FORWARD GEOCODE (SEARCH)
     (address text -> lat/lng list)
  ========================= */

  async search(
    query: string
  ): Promise<Array<{ label: string; lat: number; lng: number }>> {

    if (!query || query.trim().length < 3) {
      return [];
    }

    const override = this.matchKnownArea(query);

    try {

      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&countrycodes=bd&q=${encodeURIComponent(query)}`
      );

      const data = await res.json();

      const results = (data || []).map((d: any) => ({
        label: d.display_name,
        lat: parseFloat(d.lat),
        lng: parseFloat(d.lon)
      }));

      // পরিচিত এলাকা হলে সেটাকে সবসময় সবার উপরে রাখা হয়,
      // Nominatim-এর ফলাফল যা-ই হোক না কেন
      return override
        ? [override, ...results]
        : results;

    } catch {

      return override ? [override] : [];
    }
  }


  /* =========================
     IP-BASED GEOLOCATION
     (Chaldal-এর মতোই — কোনো GPS permission popup ছাড়াই,
     সাইটে ঢোকা মাত্রই ইউজারের IP address থেকে আন্দাজ
     এলাকা বের করে। এটা GPS-এর মতো নির্ভুল না, কিন্তু
     permission না দিলেও কাজ করে বলে সবার জন্যই একটা
     ভালো starting guess দেয়।

     ⚠️ ip-api.com ফ্রি প্ল্যানে শুধু http:// সাপোর্ট করে,
     তাই HTTPS সাইটে mixed-content হিসেবে ব্লক হয়ে যায়।
     এর বদলে ipwho.is ব্যবহার করা হয়েছে — একই রকম ফ্রি/
     no-API-key সার্ভিস, কিন্তু পুরোপুরি HTTPS সাপোর্ট করে।
     এটা fail করলে geojs.io কে ফলব্যাক হিসেবে চেষ্টা করা হয়।
  ========================= */

  async ipLocate(): Promise<
    { area: string; city: string; lat: number | null; lng: number | null } | null
  > {

    // প্রাইমারি: ipwho.is
    try {

      const res = await fetch('https://ipwho.is/');
      const data = await res.json();

      if (data && data.success !== false) {

        return {
          area: data.city || '',
          city: data.region || data.country || '',
          lat: typeof data.latitude === 'number' ? data.latitude : null,
          lng: typeof data.longitude === 'number' ? data.longitude : null
        };
      }

    } catch {
      // নিচে ফলব্যাক চেষ্টা হবে
    }

    // ফলব্যাক: geojs.io
    try {

      const res = await fetch('https://get.geojs.io/v1/ip/geo.json');
      const data = await res.json();

      if (data) {

        return {
          area: data.city || '',
          city: data.region || data.country || '',
          lat: data.latitude ? parseFloat(data.latitude) : null,
          lng: data.longitude ? parseFloat(data.longitude) : null
        };
      }

    } catch {
      // দুটোই ব্যর্থ হলে null রিটার্ন হবে, caller আগের
      // default (localStorage / "Dhaka") ব্যবহার করবে
    }

    return null;
  }
}
